import { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  MenuItem,
  Select,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useLocation,
} from "react-router-dom";
import { ExpandMore, Menu as MenuIcon } from "@mui/icons-material";

import MedictaHome from "./Home";
import DownloadPage from "./Download";
import LicensePage from "./Licence";
import About from "./About";
import Terms from "./Terms";
import Privacy from "./Privacy";
import Support from "./Support";
import SignUp from "./SignUp";
import EmailValidation from "./EmailValidation";
import SignIn from "./SignIn";
import Account from "./Account";
import PaymentPage from "./Payment";
import ThankYouPage from "./ThankYouPage";
import ResetPassword from "./ResetPassword";
import CreatePassword from "./CreatePassword";
import ClickRedirect from "./ClickRedirect";

import locales from "../locales.json";
import { useLang } from "../context/LangContext"; // Importer le hook pour la langue
import ResetPasswordEmailSent from "./ResetPasswordEmailSent";
import ForgotPassword from "./ForgotPassword";
import LicenseAdjustmentPage from "./LicenseAdjustmentPage";
import AdminPage from "./AdminPage";
import FAQ from "./FAQ";
import Contact from "./Contact";

function MedictaAppContent({
  isAuthenticated,
  setIsAuthenticated,
  lang,
  changeLanguage,
}) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Breakpoints personnalisés
  const isBelow1350 = useMediaQuery("(max-width:1350px)");
  const isBelow1250 = useMediaQuery("(max-width:1250px)");
  // SUPPR: isBelow1150 et filtrage du lien support
  // const isBelow1150 = useMediaQuery("(max-width:1150px)");

  // Navigation principale (on ne supprime plus jamais SUPPORT)
  const navLinks = [
    { key: "license", label: locales.nav_license[lang], to: "/license" },
    { key: "download", label: locales.nav_download[lang], to: "/download" },
    { key: "support", label: locales.support[lang], to: "/support" },
  ];

  // Libellés dynamiques (versions courtes)
  const tryFreeLabel = isBelow1350
    ? locales.home?.try_free_small?.[lang] || "Essai"
    : locales.home?.try_free?.[lang] || "Essai gratuit";

  const signInLabel = isBelow1250
    ? locales.nav_signin_short?.[lang] || locales.nav_signin[lang]
    : locales.nav_signin[lang];

  const signUpLabel = isBelow1250
    ? locales.nav_signup_short?.[lang] || locales.nav_signup[lang]
    : locales.nav_signup[lang];

  const authLinks = isAuthenticated
    ? [
        {
          label: locales.nav_signout[lang],
          action: () => {
            localStorage.removeItem("auth_token");
            setIsAuthenticated(false);
          },
        },
        {
          label: locales.nav_account[lang],
          to: "/account",
        },
      ]
    : [
        { label: signInLabel, to: "/signin" },
        { label: signUpLabel, to: "/signup", contained: true },
      ];

  return (
    <>
      <AppBar position="fixed" sx={{ zIndex: 1300 }}>
        <Toolbar
          sx={{
            minHeight: { xs: 56, md: 64 }, // réduit la hauteur du header
            gap: { xs: 1, md: 0 },
            px: { xs: 1, md: 2, lg: 4 },
            py: 0, // enlève le surplus vertical
          }}
        >
          {/* Hamburger mobile */}
          <Box sx={{ display: { xs: "flex", md: "none" }, mr: 1 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setDrawerOpen((o) => !o)} // toggle au clic
              aria-label="menu"
              aria-expanded={drawerOpen ? "true" : "false"}
              size="large"
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
              flexGrow: { xs: 1, md: 0 },
              minWidth: 0,
            }}
          >
            <img
              src="/icon192.png"
              alt="Logo Medicta"
              style={{ width: 40, height: 40, marginRight: 8 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "1.05rem", md: "1.15rem" },
                whiteSpace: "nowrap",
              }}
            >
              Medicta
            </Typography>
          </Box>

          {/* Nav desktop */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexGrow: 1,
              alignItems: "center",
              ml: { md: 2, lg: 4 },
              gap: { md: 1, lg: 1.5 },
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {navLinks.map((link) => (
              <Button
                key={link.to}
                color="inherit"
                component={Link}
                to={link.to}
                disableElevation
                sx={{
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  px: { md: 1.4, lg: 2 },
                  fontSize: { md: ".82rem", lg: ".9rem" },
                  lineHeight: 1.2,
                  minHeight: 38,
                }}
              >
                {link.label}
              </Button>
            ))}

            {location.pathname !== "/license" && (
              <Button
                component={Link}
                to="/license"
                color="secondary"
                variant="contained"
                disableElevation
                sx={{
                  fontWeight: "bold",
                  mx: { md: 0.6, lg: 1 },
                  px: { md: 2.6, lg: 3.6 },
                  py: { md: 0.65, lg: 0.9 },
                  borderRadius: 999,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  fontSize: { md: ".72rem", lg: ".8rem" },
                  letterSpacing: ".4px",
                  minHeight: 38,
                  //maxWidth: { md: 155, lg: 220 },
                  //overflow: "hidden",
                  //textOverflow: "ellipsis",
                }}
              >
                {tryFreeLabel}
              </Button>
            )}
          </Box>

          {/* Auth desktop */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              gap: { md: 0.8, lg: 1 },
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {authLinks.map((link) =>
              link.action ? (
                <Button
                  key={link.label}
                  color="inherit"
                  variant="outlined"
                  onClick={link.action}
                  sx={{
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    px: { md: 1.3, lg: 1.8 },
                    fontSize: { md: ".7rem", lg: ".78rem" },
                    minHeight: 38,
                  }}
                >
                  {link.label}
                </Button>
              ) : (
                <Button
                  key={link.label}
                  color={link.contained ? "secondary" : "inherit"}
                  variant={link.contained ? "contained" : "outlined"}
                  component={Link}
                  to={link.to}
                  sx={{
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    px: { md: 1.3, lg: 1.8 },
                    fontSize: { md: ".7rem", lg: ".78rem" },
                    minHeight: 38,
                    borderRadius: link.contained ? 2 : 1,
                  }}
                >
                  {link.label}
                </Button>
              ),
            )}
          </Box>

          {/* Langue */}
          <Select
            value={lang}
            onChange={(e) => changeLanguage(e.target.value)}
            sx={{
              color: "inherit",
              ml: 1,
              height: 40,
              fontSize: { md: ".7rem", lg: ".8rem" },
              "& .MuiOutlinedInput-notchedOutline": { borderWidth: 1 },
              whiteSpace: "nowrap",
              flexShrink: 0,
              minWidth: 70,
            }}
            IconComponent={(props) => (
              <ExpandMore {...props} sx={{ color: "white" }} />
            )}
          >
            <MenuItem value="fr">FR</MenuItem>
            <MenuItem value="de">DE</MenuItem>
            <MenuItem value="it">IT</MenuItem>
            <MenuItem value="en">EN</MenuItem>
          </Select>
        </Toolbar>
      </AppBar>

      {/* Drawer pour mobile */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ display: { xs: "block", md: "none" } }}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={() => setDrawerOpen(false)}
        >
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Box
                    component={Link}
                    to="/"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      textDecoration: "none",
                      color: "inherit",
                      gap: 1,
                    }}
                  >
                    <img
                      src="/logo192.png"
                      alt="Logo Medicta"
                      style={{ width: "32px", height: "32px" }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Medicta
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
            {navLinks.map((link) => (
              <ListItem key={link.to} disablePadding>
                <ListItemButton component={Link} to={link.to}>
                  <ListItemText primary={link.label} />
                </ListItemButton>
              </ListItem>
            ))}
            {/* Bouton Essai Gratuit sur mobile, sauf sur /license */}
            {location.pathname !== "/license" && (
              <ListItem disablePadding>
                <ListItemButton component={Link} to="/license">
                  <ListItemText
                    primary={
                      <Typography
                        color="secondary"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "1rem",
                          textAlign: "center",
                        }}
                      >
                        {locales.home?.try_free_small?.[lang] ||
                          "Essai gratuit"}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
            {authLinks.map((link) =>
              link.action ? (
                <ListItem key={link.label} disablePadding>
                  <ListItemButton onClick={link.action}>
                    <ListItemText primary={link.label} />
                  </ListItemButton>
                </ListItem>
              ) : (
                <ListItem key={link.label} disablePadding>
                  <ListItemButton component={Link} to={link.to}>
                    <ListItemText primary={link.label} />
                  </ListItemButton>
                </ListItem>
              ),
            )}
          </List>
        </Box>
      </Drawer>

      {/* Décale le contenu principal pour ne pas passer sous le header */}
      <Box
        sx={{
          mt: { xs: 8, md: 9 },
          px: { xs: 2, md: 0 }, // Padding horizontal sur mobile
          maxWidth: "1200px", // Largeur max sur desktop
          mx: "auto", // Centre sur desktop
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Routes>
          <Route
            path="/license"
            element={<LicensePage isAuthenticated={isAuthenticated} />}
          />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/" element={<MedictaHome />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/email-validation" element={<EmailValidation />} />
          <Route
            path="/signin"
            element={<SignIn setIsAuthenticated={setIsAuthenticated} />}
          />
          <Route path="/account" element={<Account />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route
            path="/reset-password-email-sent"
            element={<ResetPasswordEmailSent />}
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/create-password/:token/:email"
            element={<CreatePassword />}
          />
          <Route
            path="/license-adjustment"
            element={<LicenseAdjustmentPage />}
          />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/click" element={<ClickRedirect />} />
        </Routes>
      </Box>
    </>
  );
}

export default function MedictaApp() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { lang, changeLanguage } = useLang();

  useEffect(() => {
    localStorage.setItem("lang", lang);
  }, [lang]);

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    setIsAuthenticated(!!token);
  }, []);

  useEffect(() => {
    // Empêche le zoom initial et force le viewport à 100% sur mobile
    const viewport = document.querySelector("meta[name=viewport]");
    if (viewport) {
      viewport.setAttribute(
        "content",
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no",
      );
    } else {
      const meta = document.createElement("meta");
      meta.name = "viewport";
      meta.content =
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no";
      document.head.appendChild(meta);
    }
  }, []);

  if (!locales) {
    return <div>Chargement...</div>;
  }

  return (
    <Router>
      <MedictaAppContent
        isAuthenticated={isAuthenticated}
        setIsAuthenticated={setIsAuthenticated}
        lang={lang}
        changeLanguage={changeLanguage}
      />
    </Router>
  );
}
